import { CalendarTwoTone, RobotOutlined, StarFilled, UserOutlined, WarningOutlined } from "@ant-design/icons";
import { Button, Tag, Tooltip } from "antd";
import moment from "moment";
import React from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faEye, faClockRotateLeft, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import CypressTestIds from "../../../cypress/CypressTestIds";

/**
 * DataTagCard Molecule - Reusable card component for displaying data tags
 *
 * @param {Object} dataTag - The data tag object
 * @param {Object} provided - React Beautiful DND provided props
 * @param {Array} pinnedTags - Array of pinned tag IDs
 * @param {Function} handlePinTag - Function to handle pin/unpin action
 * @param {Object} permission - Permission object
 * @param {Function} onEditDataTag - Function to handle edit action
 * @param {Function} onViewDataTag - Function to handle view action
 * @param {Function} onViewDataTagHistory - Function to handle history view
 * @param {Function} onDeleteDataTag - Function to handle delete action
 * @param {Function} getBusinessAreaColors - Function to get business area colors
 */
const DataTagCard = ({ dataTag, provided, pinnedTags, handlePinTag, permission, onEditDataTag, onViewDataTag, onViewDataTagHistory, onDeleteDataTag, getBusinessAreaColors }) => {
    const { bg, text, border } = getBusinessAreaColors(dataTag.businessAreaName);
    const isUnassigned = dataTag.businessAreaName === "Unassigned";
    const isPinned = pinnedTags.includes(dataTag.tagId);
    const version = Number(dataTag?.version);
    const isInitialVersion = version === 0;
    const primaryDate = isInitialVersion ? dataTag?.createdDate || dataTag?.updatedDate : dataTag?.updatedDate || dataTag?.createdDate;
    const primaryEmail = isInitialVersion ? dataTag?.createdByEmail || dataTag?.updatedByEmail : dataTag?.updatedByEmail || dataTag?.createdByEmail;
    const createdOnDate = dataTag?.createdDate;

    return (
        <div
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            ref={provided.innerRef}
            className={`group relative flex transform flex-col overflow-hidden rounded-lg border bg-white shadow-md transition duration-200 ease-in-out hover:-translate-y-0.5 hover:shadow-xl ${
                isUnassigned ? "border border-dashed border-rose-700 bg-rose-50" : "border-gray-200 bg-white"
            }`}
        >
            {/* Header Section */}
            <div className="flex items-center justify-between rounded-t-lg bg-blue-100 p-3">
                <div className="flex flex-row items-center">
                    <Tooltip title={dataTag.tagName}>
                        <h3 className="max-w-[220px] truncate text-lg font-bold text-blue-600">{dataTag.tagName}</h3>
                    </Tooltip>

                    {/* Business Area Badge */}
                    <span
                        className={`mx-2 mt-1 rounded-full px-2 py-1 text-xs font-medium ${
                            isUnassigned ? "border border-gray-400 bg-gray-200 italic text-gray-600" : `${bg} ${text} border ${border}`
                        }`}
                    >
                        {dataTag.businessAreaName.toLowerCase()}
                    </span>

                    {/* AI Generated Badge */}
                    {dataTag.isAIgenerated && (
                        <Tooltip title="This tag was generated using AI">
                            <Tag
                                icon={<RobotOutlined />}
                                color="purple"
                                className="rounded-full px-2 py-0.5 text-sm font-semibold"
                                style={{
                                    background: "linear-gradient(90deg, #8c52ff, #409fed)",
                                    color: "#fff",
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.4)",
                                }}
                            >
                                AI
                            </Tag>
                        </Tooltip>
                    )}

                    {isUnassigned && (
                        <Tooltip title="This tag must be assigned to a business area before use">
                            <WarningOutlined className="text-2xl text-red-500" />
                        </Tooltip>
                    )}
                </div>

                <div className="flex space-x-1">
                    <Tooltip title={isPinned ? "Remove from Favorites" : "Mark as Favorite"}>
                        <StarFilled
                            className={`${isPinned ? "text-yellow-400" : "font-extrabold text-gray-400"} cursor-pointer text-xl hover:text-yellow-500`}
                            onClick={() => handlePinTag(dataTag.tagId)}
                        />
                    </Tooltip>
                </div>
            </div>

            <div className="flex flex-grow flex-col overflow-hidden p-4 hover:bg-slate-50">
                <Tooltip title={dataTag.tagDescription} placement="bottomLeft" classNames={{ root: "max-w-md" }}>
                    <div className="mb-3 line-clamp-3 h-16 text-ellipsis text-base font-normal leading-5 text-gray-800">{dataTag.tagDescription}</div>
                </Tooltip>
                <div className="mt-auto flex flex-col items-start space-y-1.5 rounded-md border-t border-gray-100 pt-3 text-gray-600">
                    {primaryDate && (
                        <div className="flex flex-wrap items-center gap-2 text-xs font-medium sm:text-sm">
                            <CalendarTwoTone className="text-blue-500" />
                            <Tooltip title={moment.utc(primaryDate).toDate().toLocaleString()}>
                                <span>
                                    {isInitialVersion ? "Created" : "Updated"} {moment.utc(primaryDate).fromNow()}
                                </span>
                            </Tooltip>
                            {createdOnDate && (
                                <>
                                    <span className="text-gray-400">•</span>
                                    <Tooltip title={moment.utc(createdOnDate).toDate().toLocaleString()}>
                                        <span className="hidden sm:inline">Created on {moment.utc(createdOnDate).toDate().toLocaleDateString()}</span>
                                    </Tooltip>
                                </>
                            )}
                        </div>
                    )}
                    {primaryEmail && (
                        <div className="flex items-start gap-2 text-xs sm:text-sm">
                            <UserOutlined className={`${isInitialVersion ? "text-gray-500" : "text-green-500"} mt-0.5`} />
                            <div className="leading-5">
                                <span className="font-medium">{isInitialVersion ? "Created by" : "Updated by"}</span> <span className="break-all">{primaryEmail}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 p-3 transition-colors duration-200 group-hover:bg-gray-100">
                <div className="flex space-x-2">
                    <Tooltip title="Edit Data Classifier Tags">
                        <Button
                            icon={<FontAwesomeIcon icon={faPencil} className="text-blue-500" />}
                            disabled={!permission.canEdit}
                            onClick={() => onEditDataTag(dataTag.tagId)}
                            data-testid={CypressTestIds.DATATAGS_DATATAGLIST_EDIT_BUTTON}
                        />
                    </Tooltip>

                    <Tooltip title="View Data Classifier Tags">
                        <Button
                            icon={<FontAwesomeIcon icon={faEye} className="text-blue-500" />}
                            disabled={!permission.canView}
                            onClick={() => onViewDataTag(dataTag.tagId)}
                            data-testid={CypressTestIds.DATATAGS_DATATAGLIST_VIEW_BUTTON}
                        />
                    </Tooltip>

                    <Tooltip title="View Data Classifier History">
                        <Button icon={<FontAwesomeIcon icon={faClockRotateLeft} className="text-blue-500" />} disabled={!permission.canView} onClick={() => onViewDataTagHistory(dataTag.tagId)} />
                    </Tooltip>

                    <Tooltip title="Delete Data Classifier Tags">
                        <Button
                            icon={<FontAwesomeIcon icon={faTrashCan} className="text-blue-500" />}
                            disabled={!permission.canDelete}
                            onClick={() => onDeleteDataTag(dataTag)}
                            data-testid={CypressTestIds.DATATAGS_DATATAGLIST_HISTORY_BUTTON}
                        />
                    </Tooltip>
                </div>
            </div>
        </div>
    );
};

export default React.memo(DataTagCard);
